import type React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import styled, { useTheme } from 'styled-components';
import useConfig from 'app/config';

import { routes } from 'app/routes/constants';
import { trackEvent } from 'src/app/utils/analytics/amplitude/tracker';
import { PlanTrackingLocation } from 'src/app/utils/analytics/amplitude/types';

import GenericPage from 'app/ui/components/layouts/generic-page';
import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import VideoPlayer from 'app/ui/components/molecules/video-component';
import { AccordionContent, AccordionItem } from 'app/ui/components/molecules/accordion';
import { PlusIcon, MinusIcon } from 'app/ui/components/atoms/plus-minus';
import WorkoutRail from 'app/ui/components/molecules/workout-rail';
import Button from 'app/ui/components/atoms/button';
import PopupDialog from 'app/ui/components/molecules/popup-dialog';
import MoreTrainingPlans from 'app/ui/components/molecules/more-training-plans';
import TrainingPlanInstanceTracker from 'app/ui/components/molecules/training-plan-instance-tracker';
import ResourceListGrid from 'src/app/ui/components/molecules/resource-list-card/grid';

import Typography from 'src/app/ui/components/atoms/typography';

import {
  BreadcrumbWrapper,
  StyledBreadcrumb,
  StyledButton,
  VideosContainer,
  AccordionContainer,
  TitleWithContent,
  EndContentWithIcons,
  Icons,
  StyledAccordionTitle,
  StyledTypography,
} from 'app/pages/progressive-plan/components';

import { getOrderedWeeks, prepareWorkoutList, type PlanContext } from 'app/pages/progressive-plan-instance/utils';

import { ProgressivePlanInstance as PROGRESSIVE_PLAN_INSTANCE } from 'app/pages/progressive-plan-instance/query.gql';
import { CancelProgressivePlan as CANCEL_PROGRESSIVE_PLAN } from 'app/pages/progressive-plan-instance/mutation.gql';
import type {
  CancelProgressivePlanMutation,
  CancelProgressivePlanMutationVariables,
  ProgressivePlanInstanceQuery,
  ProgressivePlanInstanceQueryVariables,
  ProgressivePlanWeek,
  ProgressivePlanBlock,
  ProgressivePlanInstance,
} from 'app/types/graphql';

import Divider from 'src/app/ui/components/atoms/divider';
import ResourceSections from 'app/ui/components/molecules/resource-sections';

import Tooltip from 'app/ui/components/molecules/tooltip';
import { TitleWithTooltip } from 'app/ui/components/molecules/tooltip/util';
import QuestionMarkCircle from 'app/ui/components/atoms/icons/question-mark-circle';

const StyledResourceSections = styled(ResourceSections)`
  margin-bottom: ${({ theme }) => theme.spacing(18)};
`;

const StyledTrainingPlanInstanceTracker = styled(TrainingPlanInstanceTracker)`
  margin-bottom: ${({ theme }) => theme.spacing(10)};
  cursor: default;
`;

const BlockIntroText = styled(Typography)`
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const PlaybookTitle = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const ProgressivePlanInstancePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();

  const { config } = useConfig();
  const resourceListId = config.REACT_APP_ROOT_RESOURCE_LIST_ID;

  const [openBlockIds, setOpenBlockIds] = useState<Set<string>>(new Set());
  const [dialogConfig, setDialogConfig] = useState<{
    primaryText: string;
    secondaryText?: string;
    primaryButton?: React.ReactNode;
    secondaryButton?: React.ReactNode;
  } | null>(null);

  const { data, loading, error } = useQuery<ProgressivePlanInstanceQuery, ProgressivePlanInstanceQueryVariables>(
    PROGRESSIVE_PLAN_INSTANCE,
    {
      variables: { id, resourceListId },
      skip: !id,
    },
  );

  const [cancelPlan] = useMutation<CancelProgressivePlanMutation, CancelProgressivePlanMutationVariables>(
    CANCEL_PROGRESSIVE_PLAN,
    {
      update: (cache) => {
        cache.evict({ fieldName: 'allProgressivePlanInstances' });
        cache.evict({ fieldName: 'allProgressivePlans' });
        cache.gc();
      },
      onCompleted: () => {
        setDialogConfig({
          primaryText: intl.formatMessage({
            id: 'dialog.trainingPlanCancelledPrimary',
            defaultMessage: 'Training plan cancelled successfully',
          }),
          primaryButton: (
            <Button
              label={intl.formatMessage({ id: 'dialog.close', defaultMessage: 'Close' })}
              onClick={() => {
                navigate(routes.TRAINING_PLANS);
                setDialogConfig(null);
              }}
            />
          ),
        });
      },
      onError: (error) => {
        console.error('Error cancelling plan:', error);
      },
    },
  );

  const handleCancelPlan = () => {
    if (id && plan) {
      trackEvent('end_plan_cta_clicked', {
        plan_id: plan.id,
        plan_title: plan.name,
        plan_instance_id: id,
      });
      cancelPlan({ variables: { input: { progressivePlanInstanceId: id } } });
    }
  };

  const instance = data?.planInstance;
  const plan = instance?.progressivePlan;
  const resourceList = data?.resourceList;

  const planContext = useMemo((): PlanContext | null => {
    if (!instance) {
      return null;
    }
    const today = instance.todaysWorkout?.progressivePlanWorkout;
    const tomorrow = instance.upcomingWorkouts?.[0]?.progressivePlanWorkout;

    return {
      currentBlockOrder: instance.currentBlock?.displayOrder || 0,
      currentWeekId: instance.currentWeek?.id,
      currentWeekNumber: instance.currentWeek?.weekNumber || 0,
      todaysWorkoutId: today?.id,
      todaysWorkoutWeekId: today?.progressivePlanWeek?.id,
      todaysDayOfWeek: today?.dayOfWeek || 0,
      tomorrowsWorkoutId: tomorrow?.id,
      tomorrowsWorkoutWeekId: tomorrow?.progressivePlanWeek?.id,
    };
  }, [instance]);

  const blocks = useMemo(() => (plan?.blocks || []).filter((b): b is NonNullable<typeof b> => !!b), [plan]);

  useEffect(() => {
    const currentBlockId = instance?.currentBlock?.id;
    if (currentBlockId) {
      setOpenBlockIds(new Set([currentBlockId]));
    }
  }, [instance?.currentBlock?.id]);

  if (loading) {
    return <Loading />;
  }
  if (error || !instance || !plan || !planContext) {
    return <ErrorComponent />;
  }

  return (
    <GenericPage pageTitle={plan.name}>
      <BreadcrumbWrapper>
        <StyledBreadcrumb contentTitle={plan.name} />
        <StyledButton
          label={intl.formatMessage({ id: 'progressivePlan.endPlan', defaultMessage: 'End plan' })}
          onClick={handleCancelPlan}
        />
      </BreadcrumbWrapper>

      <StyledTrainingPlanInstanceTracker planInstance={instance as ProgressivePlanInstance} />

      <AccordionContainer>
        {blocks.map((block, index) => {
          const isOpen = openBlockIds.has(block.id);
          const blockWeeks = (block.weeks || []).filter((w): w is NonNullable<typeof w> => !!w);
          const weeks = getOrderedWeeks(
            blockWeeks as ProgressivePlanWeek[],
            block.id === instance.currentBlock?.id,
            planContext.currentWeekId,
          );
          return (
            <AccordionItem key={block.id}>
              <StyledAccordionTitle
                $isOpen={isOpen}
                onClick={() => {
                  setOpenBlockIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(block.id)) {
                      next.delete(block.id);
                    } else {
                      next.add(block.id);
                    }
                    return next;
                  });
                }}
              >
                <TitleWithContent $isOpen={isOpen}>
                  <TitleWithTooltip>
                    <StyledTypography variant="h6" $isPast={block.displayOrder < planContext.currentBlockOrder}>
                      {`${index + 1}. ${block.name}`}
                    </StyledTypography>
                    {instance?.currentBlock?.id === block.id && (
                      <Tooltip tooltipText={intl.formatMessage({ id: 'trainingPlan.tooltip.phase' })} usePortal={true}>
                        <QuestionMarkCircle color={isOpen ? theme.colors.focoltone : undefined} />
                      </Tooltip>
                    )}
                  </TitleWithTooltip>
                </TitleWithContent>
                <EndContentWithIcons>
                  <Icons>
                    {isOpen ? <MinusIcon color={theme.colors.focoltone} /> : <PlusIcon color={theme.colors.white} />}
                  </Icons>
                </EndContentWithIcons>
              </StyledAccordionTitle>
              <AccordionContent $isOpen={isOpen}>
                {block.introText && (
                  <BlockIntroText variant="caption" color={theme.colors.lightGray}>
                    {block.introText}
                  </BlockIntroText>
                )}
                {block.introVideo && (block.introVideo.url || block.introVideo.signedUrl) && (
                  <VideosContainer>
                    <VideoPlayer
                      src={block.introVideo.signedUrl || block.introVideo.url || ''}
                      title={block.name}
                      thumbnail={block.introVideo.thumbnail?.signedUrl || block.introVideo.thumbnail?.url}
                      trackEventOnClickPlay={() =>
                        trackEvent('plan_video_played', {
                          plan_id: plan.id,
                          plan_title: plan.name,
                          plan_block_id: block.id,
                          video_title: block.name,
                          video_id: block.introVideo?.id,
                          location: PlanTrackingLocation.PLAN_INSTANCE,
                        })
                      }
                    />
                  </VideosContainer>
                )}
                {isOpen &&
                  weeks.map((week, index) => {
                    const { workoutList, isPastWeek } = prepareWorkoutList(
                      week,
                      block as ProgressivePlanBlock,
                      planContext,
                      intl,
                    );

                    return (
                      <WorkoutRail
                        key={week.id}
                        title={
                          <TitleWithTooltip>
                            <StyledTypography variant="h6" $isPast={isPastWeek}>
                              {week.title || `Week ${week.weekNumber}`}
                            </StyledTypography>
                            {index === 0 && (
                              <Tooltip
                                tooltipText={intl.formatMessage({ id: 'trainingPlan.tooltip.week' })}
                                usePortal={true}
                              >
                                <QuestionMarkCircle color={isPastWeek ? theme.colors.focoltone : undefined} />
                              </Tooltip>
                            )}
                          </TitleWithTooltip>
                        }
                        description={week.shortDescription}
                        workouts={workoutList}
                        showProgress={false}
                        cardVariant="simplified"
                        onCardClick={(workout) => {
                          trackEvent('plan_workout_card_clicked', {
                            plan_id: plan.id,
                            plan_title: plan.name,
                            lesson_id: workout.id,
                            lesson_title: workout.title,
                          });
                        }}
                      />
                    );
                  })}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </AccordionContainer>

      {plan.overviewVideo && (plan.overviewVideo.url || plan.overviewVideo.signedUrl) && (
        <VideosContainer>
          <VideoPlayer
            src={plan.overviewVideo.signedUrl || plan.overviewVideo.url || ''}
            title={intl.formatMessage({ id: 'progressivePlan.overview', defaultMessage: 'Overview' })}
            thumbnail={plan.overviewVideo.thumbnail?.signedUrl || plan.overviewVideo.thumbnail?.url}
            trackEventOnClickPlay={() =>
              trackEvent('plan_video_played', {
                plan_id: plan.id,
                plan_title: plan.name,
                video_title: intl.formatMessage({ id: 'progressivePlan.overview', defaultMessage: 'Overview' }),
                video_id: plan.overviewVideo?.id,
                location: PlanTrackingLocation.PLAN_INSTANCE,
              })
            }
          />
        </VideosContainer>
      )}

      {plan.resource?.sections && plan.resource.sections.length > 0 && (
        <>
          <Divider variant="sm" fullWidth={true} />
          <StyledResourceSections sections={plan.resource.sections} resourceId={plan.resource.id} />
          {resourceList && (
            <>
              <PlaybookTitle variant="h4">Playbook Quick Links</PlaybookTitle>
              <ResourceListGrid resourceLists={resourceList.resourceLists || []} />
            </>
          )}
        </>
      )}

      <Divider variant="sm" fullWidth={true} />

      <MoreTrainingPlans currentPlanId={plan.id} />

      <PopupDialog
        isOpen={!!dialogConfig}
        primaryText={dialogConfig?.primaryText || ''}
        secondaryText={dialogConfig?.secondaryText}
        primaryButton={dialogConfig?.primaryButton}
        secondaryButton={dialogConfig?.secondaryButton}
      />
    </GenericPage>
  );
};

export default ProgressivePlanInstancePage;
